import {useCallback, useEffect, useRef, useState} from 'react'

export interface AutoPlayButtonProps {
  onTimeout: () => void
  ariaLabel: {
    play: string
    pause: string
  }
}

const defaultProps: Partial<AutoPlayButtonProps> = {}

const COUNTDOWN_DURATION = 5 // seconds
export const TRANSITION_DURATION = 0.4 // seconds
const CIRCLE_PATH_LENGTH = 502

const PlayIcon = () => (
  <svg viewBox="0 0 15 16" fill="none" role="presentation">
    <path
      d="M2.24953 1.69194C1.82266 1.45967 1.30273 1.76868 1.30273 2.25466V13.7452C1.30273 14.2312 1.82266 14.5402 2.24954 14.3079L12.8084 8.5626C13.2543 8.32 13.2543 7.67983 12.8084 7.4372L2.24953 1.69194ZM0.0214844 2.25466C0.0214844 0.796719 1.58126 -0.130311 2.8619 0.566509L13.4208 6.31176C14.7585 7.03966 14.7585 8.9602 13.4208 9.6881L2.86191 15.4333C1.58127 16.1301 0.0214844 15.2031 0.0214844 13.7452V2.25466Z"
      fill="currentColor"
    />
  </svg>
)

const PauseIcon = () => (
  <svg viewBox="0 0 14 16" fill="none" role="presentation">
    <path
      d="M1.65788 0.3125C0.832326 0.3125 0.163086 0.98174 0.163086 1.80729V14.1927C0.163086 15.0183 0.832326 15.6875 1.65788 15.6875H4.64746C5.47302 15.6875 6.14226 15.0183 6.14226 14.1927V1.80729C6.14226 0.98174 5.47302 0.3125 4.64746 0.3125H1.65788ZM1.44434 1.80729C1.44434 1.68936 1.53995 1.59375 1.65788 1.59375H4.64746C4.7654 1.59375 4.86101 1.68936 4.86101 1.80729V14.1927C4.86101 14.3106 4.7654 14.4062 4.64746 14.4062H1.65788C1.53995 14.4062 1.44434 14.3106 1.44434 14.1927V1.80729ZM9.34536 0.3125C8.51986 0.3125 7.85056 0.98174 7.85056 1.80729V14.1927C7.85056 15.0183 8.51986 15.6875 9.34536 15.6875H12.335C13.1606 15.6875 13.8298 15.0183 13.8298 14.1927V1.80729C13.8298 0.98174 13.1606 0.3125 12.335 0.3125H9.34536ZM9.13186 1.80729C9.13186 1.68936 9.22746 1.59375 9.34536 1.59375H12.335C12.4529 1.59375 12.5485 1.68936 12.5485 1.80729V14.1927C12.5485 14.3106 12.4529 14.4062 12.335 14.4062H9.34536C9.22746 14.4062 9.13186 14.3106 9.13186 14.1927V1.80729Z"
      fill="currentColor"
    />
  </svg>
)

const AutoPlayButton: React.FC<AutoPlayButtonProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {onTimeout, ariaLabel} = initializedProps

  const [isPaused, setIsPaused] = useState<boolean>(false)
  const circleRef = useRef<SVGCircleElement>(null)

  const onPause = useCallback(() => {
    setIsPaused(state => !state)
  }, [])

  useEffect(() => {
    if (!isPaused) {
      if (circleRef.current) {
        for (const animation of circleRef.current.getAnimations()) {
          animation.play()
        }
      }
    } else {
      if (circleRef.current) {
        for (const animation of circleRef.current.getAnimations()) {
          animation.pause()
        }
      }
    }
  }, [isPaused])

  useEffect(() => {
    const animateCircle = async () => {
      await circleRef.current?.animate([{strokeDashoffset: 0}, {strokeDashoffset: CIRCLE_PATH_LENGTH}], {
        duration: COUNTDOWN_DURATION * 1000,
        fill: 'forwards',
      }).finished

      onTimeout()

      await circleRef.current?.animate([{strokeDashoffset: CIRCLE_PATH_LENGTH}, {strokeDashoffset: 0}], {
        duration: TRANSITION_DURATION * 1000,
        easing: 'ease-out',
        fill: 'forwards',
      }).finished

      animateCircle()
    }

    animateCircle()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // TODO: Improve accessibility
  return (
    <button className="AutoPlayButton" onClick={onPause} aria-label={isPaused ? ariaLabel.play : ariaLabel.pause}>
      <svg viewBox="0 0 200 200" role="presentation">
        <defs>
          <linearGradient id="gradientStroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#b1b9c2" stopOpacity="1" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        <circle
          ref={circleRef}
          cx="100"
          cy="100"
          r="80"
          fill="transparent"
          stroke="url(#gradientStroke)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={CIRCLE_PATH_LENGTH}
          strokeDashoffset="0"
        />
      </svg>

      {isPaused ? <PlayIcon /> : <PauseIcon />}
    </button>
  )
}

export default AutoPlayButton

try{ PlayIcon.displayName ||= 'PlayIcon' } catch {}
try{ PauseIcon.displayName ||= 'PauseIcon' } catch {}